/**
 * Notification Service for Thai Security Directory
 * Handles all notification-related API calls
 */

import { apiClient } from './api'
import {
  NotificationsResponse,
  UnreadCountResponse,
  NotificationActionResponse,
} from '../types/notification'

class NotificationService {
  private readonly baseURL = '/api/notifications'

  /**
   * ดึงรายการ notifications ของผู้ใช้ปัจจุบัน
   * @param page - หน้าที่ต้องการดึง (default: 1)
   * @param perPage - จำนวน notifications ต่อหน้า (default: 10)
   * @param unreadOnly - แสดงเฉพาะ unread (default: false)
   * @returns NotificationsResponse พร้อม pagination
   */
  async getMyNotifications(
    page: number = 1,
    perPage: number = 10,
    unreadOnly: boolean = false
  ): Promise<NotificationsResponse> {
    try {
      const response = await apiClient.get(`${this.baseURL}/my`, {
        params: {
          page,
          per_page: perPage,
          unread_only: unreadOnly
        }
      })
      return response.data
    } catch (error) {
      console.error('Error fetching notifications:', error)
      throw error
    }
  }

  /**
   * Mark notification เป็น read
   * @param notificationId - ID ของ notification
   * @returns NotificationActionResponse
   */
  async markAsRead(notificationId: string): Promise<NotificationActionResponse> {
    try {
      const response = await apiClient.post(`${this.baseURL}/${notificationId}/read`)
      return response.data
    } catch (error) {
      console.error(`Error marking notification ${notificationId} as read:`, error)
      throw error
    }
  }

  /**
   * ดึงจำนวน unread notifications
   * @returns UnreadCountResponse
   */
  async getUnreadCount(): Promise<UnreadCountResponse> {
    try {
      const response = await apiClient.get(`${this.baseURL}/unread-count`)
      return response.data
    } catch (error) {
      console.error('Error fetching unread count:', error)
      throw error
    }
  }

  /**
   * Mark ทุก notifications เป็น read
   * @returns NotificationActionResponse
   */
  async markAllAsRead(): Promise<NotificationActionResponse> {
    try {
      const response = await apiClient.post(`${this.baseURL}/mark-all-read`)
      return response.data
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      throw error
    }
  }

  /**
   * ลบ notification
   * @param notificationId - ID ของ notification
   * @returns NotificationActionResponse
   */
  async deleteNotification(notificationId: string): Promise<NotificationActionResponse> {
    try {
      const response = await apiClient.delete(`${this.baseURL}/${notificationId}`)
      return response.data
    } catch (error) {
      console.error(`Error deleting notification ${notificationId}:`, error)
      throw error
    }
  }

  /**
   * Helper: ตรวจสอบว่า notification link ควร navigate ไปที่ไหน
   * @param link - notification link จาก backend
   * @returns route path ที่ควร navigate
   */
  parseNotificationLink(link: string | null): string {
    if (!link) return '/'

    // Link มาจาก backend อาจจะเป็น relative path หรือ full URL
    // ถ้าเป็น full URL → extract path only
    try {
      const url = new URL(link)
      return url.pathname
    } catch {
      // Not a valid URL, assume it's a path
      return link.startsWith('/') ? link : `/${link}`
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService()
